/**
 * Map document -> runnable mission.
 *
 * Turns a `streetsense.map/1` document into the same `MissionBuild` the
 * hand-written district produces, so a compiled map is not a second-class
 * level: it gets the same road SDF, the same delivery-path frame, the same
 * agent models, the same encounter segmentation and the same telemetry.
 *
 * Resolution order matters and is the reason places may be route-relative:
 * roads -> route -> everything else. Once the path exists, `{ s, d }` is just
 * `offsetPoint(s, d)` on it, so props, decor and actors can all be written in
 * "metres along the route" instead of world coordinates. That resolution uses
 * the route AS WRITTEN (the layout path): the kerb pull-in bends the driven
 * line in its last few metres, and scenery beside the road does not move
 * because the robot now finishes at the kerb.
 *
 * Pure module: no DOM, no Babylon.
 */

import type { Vec2 } from "../../core/vec";
import {
  CatAgent,
  DogAgent,
  ResearchPedAgent,
  StandingPersonAgent,
  VehicleAgent,
  WalkerAgent,
  type AgentModel,
} from "../agents";
import type { EncounterConfig } from "../encounters";
import { kerbLanePoint, snapLastToKerb } from "../lane_ribbon";
import type { DecorDef, MissionBuild, MissionSection, PropDef } from "../mission";
import { pieceForward, RoadNetwork, type BandPiece, type RingPiece, type RoadPiece, type RoadType } from "../network";
import { DeliveryPath, type PathWaypoint } from "../path";
import type { ScenarioConfig, Variant } from "../types";
import { actorKind, propKind, ROAD_WIDTH } from "./registry";
import { resolveDirections } from "./route";
import {
  isPlaceRouteRef,
  isRing,
  type ActorDoc,
  type MapDoc,
  type Place,
  type RouteWaypointDoc,
  type XZ,
  type ZoneEdge,
} from "./types";

export class MapCompileError extends Error {
  constructor(
    message: string,
    readonly at?: string,
  ) {
    super(at ? `${at}: ${message}` : message);
    this.name = "MapCompileError";
  }
}

/** Research pedestrian defaults, matching the district mission's timing. */
const DEFAULT_PED_SPAWN_S = 48;
const DEFAULT_PED_WALK_S = 14;

/** How far a declared destination may sit from the raw path end and still be
 * the same delivery — beyond that the map means somewhere else. */
const DEST_SAME_M = 3;

export function compileMap(doc: MapDoc, cfg: ScenarioConfig): MissionBuild {
  // The mirrored variant flips a whole authored layout to get a second route
  // out of one map. A photographed map has no second route: mirroring it puts
  // the robot on the wrong side of a real street and hands the viewer a
  // reversed photograph as if it were the place. Real imagery wins the flip.
  const m: 1 | -1 = cfg.mirror && !doc.imagery ? -1 : 1;
  const mirrorPt = (p: Vec2): Vec2 => ({ x: p.x * m, z: p.z });
  const mirrorYaw = (yaw: number): number =>
    m > 0 ? yaw : Math.atan2(-Math.sin(yaw), Math.cos(yaw));

  // --- roads ---------------------------------------------------------------
  const pieces: RoadPiece[] = doc.roads.map((r) => {
    if (isRing(r)) {
      const ring: RingPiece = {
        kind: "ring",
        id: r.id,
        roadType: "roundabout",
        cx: r.center[0] * m,
        cz: r.center[1],
        rIn: r.innerR,
        rOut: r.outerR,
        // Counter-clockwise (right-hand traffic) is -1; mirroring flips it.
        dir: ((r.clockwise ? 1 : -1) * m) as 1 | -1,
      };
      return ring;
    }
    const band: BandPiece = {
      kind: "band",
      id: r.id,
      roadType: r.kind,
      pts: r.pts.map(([x, z]) => mirrorPt({ x, z })),
      halfWidth: (r.width ?? ROAD_WIDTH[r.kind]) / 2,
      ...(r.frame === false ? { frame: false } : {}),
      ...(r.oneWay ? { oneWay: true } : {}),
      ...(r.lanes !== undefined ? { lanes: r.lanes } : {}),
    };
    return band;
  });
  // --- route ---------------------------------------------------------------
  const wpDocs: RouteWaypointDoc[] =
    doc.route.waypoints && doc.route.waypoints.length > 0
      ? doc.route.waypoints
      : resolveDirections(doc);
  if (wpDocs.length < 2) {
    throw new MapCompileError("a route needs at least two waypoints", "route");
  }
  let network = new RoadNetwork(pieces);
  if (extendStartApron(pieces, wpDocs, m, network)) network = new RoadNetwork(pieces);
  const waypoints: PathWaypoint[] = wpDocs.map((w, i) => {
    if (isPlaceRouteRef(w.at)) {
      throw new MapCompileError(
        "route waypoints must be world [x, z] — {s, d} is relative to the route being defined",
        `route.waypoints[${i}]`,
      );
    }
    const [x, z] = w.at;
    const road = w.road ? doc.roads.find((r) => r.id === w.road) : undefined;
    const halfWidth =
      w.halfWidth ??
      (road && !isRing(road) ? (road.width ?? ROAD_WIDTH[road.kind]) / 2 : ROAD_WIDTH.alley / 2);
    return {
      x: x * m,
      z,
      halfWidth,
      ...(w.fillet !== undefined ? { fillet: w.fillet } : {}),
      ...(w.name !== undefined ? { name: w.name } : {}),
      ...(w.road !== undefined ? { piece: w.road } : {}),
    };
  });

  // The route AS WRITTEN, kept before the kerb pull-in bends its last few
  // metres. Scenery is laid out against the line the author drew: a customer
  // written at { s: 198, d: 6 } stands 6 m right of the route's own line, and
  // that must not move because the robot now finishes at the kerb instead of
  // on the centreline. Resolving it against the pulled-in route dragged decor
  // 1-4 m sideways on nine maps — on m05 a prop 1.02 m past the kerb ended up
  // 4.33 m past it, in the buildings.
  const layoutWaypoints = waypoints.map((w) => ({ ...w }));
  let layout: DeliveryPath | null = null;
  /** The pre-snap route: the frame every `{ s, d }` place is written in. */
  const layoutPath = (): DeliveryPath => (layout ??= new DeliveryPath(layoutWaypoints, []));

  // Kerb-side delivery (ADR-065 amendment K): a route's last waypoint is
  // written on the road's centreline, which would end the run — and stand the
  // end-of-route marker — in the middle of the carriageway. Move it onto the
  // painted kerb lane before the path is built, so path, corridor, guidance
  // and marker all agree.
  const lastWp = waypoints[waypoints.length - 1]!;
  const rawEnd: Vec2 = { x: lastWp.x, z: lastWp.z };
  const kerbEnd = snapLastToKerb(waypoints, network);

  const path = new DeliveryPath(
    waypoints,
    (doc.route.turns ?? []).map((t) => {
      const kind =
        m > 0 ? t.kind : t.kind === "left" ? "right" : t.kind === "right" ? "left" : t.kind;
      // A mirrored left turn is a right turn, so the map's own label is
      // wrong for it — "Turn left" on a map that now turns right.
      const flipped = kind !== t.kind;
      const fallback =
        kind === "left" ? "Turn left" : kind === "right" ? "Turn right" : kind === "arrive" ? "Deliver" : "Roundabout";
      const label = flipped ? fallback : (t.label ?? fallback);
      return { at: t.at, kind, label, ...(t.exit !== undefined ? { exit: t.exit } : {}) };
    }),
  );

  // Everything below may be written route-relative — against the LAYOUT path,
  // never against the pulled-in one the robot drives. The two are the same
  // line everywhere but the last few metres, which is exactly where scenery
  // must not be dragged along by a change to the driving line.
  const place = (p: Place, where: string): Vec2 => {
    if (isPlaceRouteRef(p)) {
      if (!Number.isFinite(p.s) || !Number.isFinite(p.d)) {
        throw new MapCompileError("route-relative place needs numeric s and d", where);
      }
      const lp = layoutPath();
      const q = lp.offsetPoint(clamp(p.s, 0, lp.length), p.d);
      return { x: q.x, z: q.z };
    }
    const [x, z] = p;
    return mirrorPt({ x, z });
  };
  const places = (ps: Place[] | undefined, where: string): Vec2[] =>
    (ps ?? []).map((p, i) => place(p, `${where}[${i}]`));

  // --- props / decor -------------------------------------------------------
  /**
   * Kinds that park PARALLEL to the kerb, because that is how they park.
   *
   * `across` is a raw geometric flag — it swaps halfW and halfD — and every
   * hand-written map got it wrong the same way: a car written as
   * `{"kind": "parked_car", "at": [4.1, 105]}` came out 4.2 m long ACROSS a
   * carriageway running the other way, broadside to the traffic and poking
   * 1.2 m past the far kerb. Two of them at once were inside each other.
   *
   * So a vehicle whose footprint the map has not specified is turned to lie
   * along the road it is beside. A map that means "slewed across the street"
   * says so with its own halfW/halfD — and a broken_down_truck is not in this
   * set at all, so the one blocking half a road goes on doing that.
   */
  const PARKS_PARALLEL = new Set(["parked_car", "parked_motorbike"]);

  /**
   * ...and the one kind that lies ACROSS the road instead: a speed bump spans
   * the carriageway, which is the perpendicular of how a car parks beside it.
   * Same guard — only when the map has not given its own footprint.
   */
  const LIES_ACROSS = new Set(["speed_bump"]);

  const obstacles: PropDef[] = (doc.props ?? []).map((p, i) => {
    const k = propKind(p.kind);
    const halfW = p.halfW ?? k.halfW;
    const halfD = p.halfD ?? k.halfD;
    const at = place(p.at, `props[${i}]`);
    // Only when the map has said nothing about the footprint or the heading.
    // `across` is ignored for these: it is a raw swap of halfW/halfD with no
    // relation to the road, and every map that set it wrote `false` — the
    // default written out, not a decision. A car left broadside across a live
    // carriageway is a wreck, not parking. A map that genuinely wants one says
    // so by giving its own halfW/halfD, which is also how the district's
    // pinned geometry stays exactly as recorded.
    const autoOrient =
      (PARKS_PARALLEL.has(p.kind) || (LIES_ACROSS.has(p.kind) && p.across === undefined)) &&
      p.halfW === undefined &&
      p.halfD === undefined;
    let across = p.across === true;
    if (autoOrient) {
      // The ROAD'S OWN DIRECTION, not the boundary normal.
      //
      // The normal is the obvious way to find "across the road" and it has a
      // hole in it: at the centre line of a road the distance field is at a
      // local extremum, so the finite difference cancels to exactly (0, 0).
      // A motorbike parked mid-alley got a zero gradient and was laid broadside
      // across the alley, silently and only there. The tangent has no such
      // degenerate point.
      const piece = network.pieces[network.deepestPiece(at.x, at.z)];
      const yaw = piece ? pieceForward(piece, at.x, at.z) : 0;
      const roadIsX = Math.abs(Math.sin(yaw)) >= Math.abs(Math.cos(yaw));
      const longIsX = halfW >= halfD;
      across = LIES_ACROSS.has(p.kind) ? roadIsX === longIsX : roadIsX !== longIsX;
    }
    return {
      kind: p.kind,
      x: at.x,
      z: at.z,
      halfW: across ? halfD : halfW,
      halfD: across ? halfW : halfD,
      height: p.height ?? k.height,
      role: p.role,
      ...(k.pit ? { pit: true } : {}),
      ...(p.unmarked ? { unmarked: true } : {}),
      ...(k.bump ? { bump: true } : {}),
    };
  });
  const decor: DecorDef[] = (doc.decor ?? []).map((d, i) => {
    const at = place(d.at, `decor[${i}]`);
    return { kind: d.kind, x: at.x, z: at.z, yaw: mirrorYaw(d.yaw ?? 0) };
  });

  // --- encounters + sections ----------------------------------------------
  const edge = (e: ZoneEdge, where: string): number => {
    if (typeof e === "number") return e;
    const [anchor, off] = e;
    const base = path.anchorOrNull(anchor);
    if (base === null) throw new MapCompileError(`unknown route anchor "${anchor}"`, where);
    return base + off;
  };
  const encDocs = doc.encounters ?? [];
  // Sections are numbered by the order they first appear, so several
  // encounters may share one (a junction turn and the traffic just past it
  // are one stretch of road to an analyst).
  const sectionOrder: string[] = (doc.sections ?? []).map((s) => s.name);
  const sectionNumber = (name: string): number => {
    const at = sectionOrder.indexOf(name);
    if (at >= 0) return at + 1;
    sectionOrder.push(name);
    return sectionOrder.length;
  };
  const encounters: EncounterConfig[] = encDocs.map((e, i) => {
    const zoneStart = edge(e.from, `encounters[${i}].from`);
    const zoneEnd = edge(e.to, `encounters[${i}].to`);
    const mid = path.point((zoneStart + zoneEnd) / 2);
    const piece = pieces.find((p) => p.id === mid.piece);
    const roadType: RoadType = e.roadType ?? (piece ? piece.roadType : "street");
    return {
      id: e.id,
      type: e.type,
      section: sectionNumber(e.section ?? e.id),
      zoneStart,
      zoneEnd,
      research: e.research === true && doc.research === true,
      agentIds: e.actors ?? [],
      roadType,
    };
  });
  const sections: MissionSection[] = doc.sections
    ? doc.sections.map((sec, i) => {
        const sStart = edge(sec.from, `sections[${i}].from`);
        const sEnd = edge(sec.to, `sections[${i}].to`);
        return {
          section: i + 1,
          name: sec.name,
          piece: sec.road ?? path.point((sStart + sEnd) / 2).piece,
          sStart,
          sEnd,
        };
      })
    : encDocs.map((e, i) => ({
        section: i + 1,
        name: e.section ?? e.id,
        piece: path.point((encounters[i]!.zoneStart + encounters[i]!.zoneEnd) / 2).piece,
        sStart: encounters[i]!.zoneStart,
        sEnd: encounters[i]!.zoneEnd,
      }));

  // --- destination ---------------------------------------------------------
  // The end of the route IS the delivery point, and it now sits on the kerb
  // lane. A map that names its own destination usually names the same spot it
  // arrives at, so that spelling gets the kerb-side end too; one that points
  // somewhere genuinely else is snapped on its own, with the arrival heading.
  const destination = ((): Vec2 => {
    const end = path.point(path.length);
    if (!doc.route.destination) return { x: end.x, z: end.z };
    const given = place(doc.route.destination, "route.destination");
    if (Math.hypot(given.x - rawEnd.x, given.z - rawEnd.z) <= DEST_SAME_M) {
      return { x: kerbEnd.x, z: kerbEnd.z };
    }
    return kerbLanePoint(network, given.x, given.z, end.theta);
  })();

  // --- actors --------------------------------------------------------------
  const makeAgents = (): AgentModel[] =>
    (doc.actors ?? []).map((a, i) => makeAgent(a, `actors[${i}]`, {
      place,
      places,
      path,
      network,
      variant: cfg.variant,
      seed: cfg.seed,
      mirror: m,
      mirrorYaw,
    }));

  return {
    mapId: doc.id,
    network,
    path,
    obstacles,
    decor,
    encounters,
    sections,
    destination,
    mirror: m,
    conditions: doc.conditions,
    imagery: doc.imagery,
    makeAgents,
  };
}

interface AgentCtxDeps {
  place(p: Place, where: string): Vec2;
  places(ps: Place[] | undefined, where: string): Vec2[];
  path: DeliveryPath;
  network: RoadNetwork;
  variant: Variant;
  seed: number;
  mirror: 1 | -1;
  mirrorYaw(yaw: number): number;
}

function makeAgent(a: ActorDoc, where: string, d: AgentCtxDeps): AgentModel {
  const kind = actorKind(a.type);
  if (!kind) throw new MapCompileError(`unknown actor type "${a.type}"`, where);
  const speed = a.speed ?? kind.speed;
  const trigger = a.triggerAt ?? null;
  const role = a.role ?? defaultRole(a);

  switch (kind.backing) {
    case "vehicle": {
      const pts = d.places(a.path, `${where}.path`);
      if (pts.length < 2) {
        throw new MapCompileError("a vehicle needs a path of at least two points", where);
      }
      return new VehicleAgent({
        ...(a.kerbIdle === false || a.motion === "reverse" ? {} : { network: d.network }),
        ...(a.motion === "reverse" ? { reverse: true } : {}),
        id: a.id,
        type: kind.vehicleType ?? "motorbike",
        role,
        waypoints: pts,
        cruise: speed,
        triggerS: trigger,
        ...(a.cueDelayS !== undefined ? { cueDelayS: a.cueDelayS } : {}),
        ...(a.holdIfRobotBehindM !== undefined ? { holdIfRobotBehindM: a.holdIfRobotBehindM } : {}),
        ...(a.canOvertake !== undefined ? { canOvertake: a.canOvertake } : {}),
        ...(a.parkAtEnd !== undefined ? { parkAtEnd: a.parkAtEnd } : {}),
        ...(kind.radius !== undefined ? { radius: kind.radius } : {}),
        ...(kind.body !== undefined ? { body: kind.body } : {}),
        ...(kind.accel !== undefined ? { accel: kind.accel } : {}),
        ...(kind.decel !== undefined ? { decel: kind.decel } : {}),
      });
    }
    case "walker": {
      const pts = d.places(a.path, `${where}.path`);
      if (pts.length < 2) {
        throw new MapCompileError("a walker needs a path of at least two points", where);
      }
      return new WalkerAgent(a.id, role, pts, speed, trigger);
    }
    case "stander": {
      if (!a.at) throw new MapCompileError("a stander needs `at`", where);
      const p = d.place(a.at, `${where}.at`);
      return new StandingPersonAgent(a.id, role, p.x, p.z, d.mirrorYaw(a.facing ?? 0));
    }
    case "cat": {
      if (!a.at || !a.to) throw new MapCompileError("a crossing pet needs `at` and `to`", where);
      const from = d.place(a.at, `${where}.at`);
      const to = d.place(a.to, `${where}.to`);
      // Where it crosses, in route progress, is where it starts from.
      const crossS = isPlaceRouteRef(a.at) ? a.at.s : d.path.project(from.x, from.z).s;
      return new CatAgent(a.id, from, to, crossS, trigger ?? Math.max(0, crossS - 20));
    }
    case "dog": {
      if (!a.at) throw new MapCompileError("a dog needs `at`", where);
      const p = d.place(a.at, `${where}.at`);
      const side: 1 | -1 = a.homeSide === "left" ? -1 : 1;
      const home = (side * d.mirror) as 1 | -1;
      const sitS = isPlaceRouteRef(a.at) ? a.at.s : d.path.project(p.x, p.z).s;
      return new DogAgent(a.id, p, home, trigger ?? Math.max(0, sitS - 18), d.path);
    }
    case "research_ped": {
      const spawnS = isPlaceRouteRef(a.at ?? { s: DEFAULT_PED_SPAWN_S, d: 0 })
        ? (a.at as { s: number }).s
        : DEFAULT_PED_SPAWN_S;
      return new ResearchPedAgent(
        a.id,
        spawnS,
        d.variant,
        d.mirror,
        d.seed,
        d.path,
        a.walkAt ?? DEFAULT_PED_WALK_S,
      );
    }
  }
}

function defaultRole(a: ActorDoc): string {
  switch (a.type) {
    case "stander":
      return "talking";
    case "cat":
    case "chicken":
      return "crossing";
    case "dog":
      return "playful";
    default:
      return "traffic";
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Road behind the starting line, so the chase camera has something under it.
 *
 * The camera sits 8 m BEHIND the robot. A map whose first road begins exactly
 * where the route begins therefore opens with the camera past the end of the
 * world: the bottom of the frame is bare ground, the carriageway and its
 * markings stop in a hard horizontal line across the middle of the screen, and
 * it reads as a broken frame rather than as a short road. It clears itself
 * once the robot has driven a few metres, which is why it was reported as
 * "only the first frames" and mistaken for a stutter through six rounds of
 * frame-timing work.
 *
 * The built-in district never showed it because its network happens to extend
 * 9.5 m behind the spawn; every authored map so far reaches only 4 m.
 *
 * This belongs to the compiler rather than to each map file: it is a
 * consequence of how far back the CAMERA sits, which no map author can be
 * expected to know or to remember. The apron extends the road's existing end
 * along its own direction, so it adds no corner and no junction — just more of
 * the same road, behind a start line nobody drives back across.
 */
const START_APRON_M = 16;
/**
 * How far behind the start the camera actually sits (m), plus room to spare.
 * Matches CAM_BACK in the renderer; a map that already provides this much is
 * left alone, which is how the built-in district — whose network happens to
 * run 9.5 m back — keeps the geometry its trajectory pins were recorded on.
 */
const CAMERA_NEEDS_M = 9;

function extendStartApron(
  pieces: RoadPiece[],
  wpDocs: RouteWaypointDoc[],
  m: 1 | -1,
  network: RoadNetwork,
): boolean {
  const first = wpDocs[0];
  const second = wpDocs[1];
  if (!first || !second || isPlaceRouteRef(first.at) || isPlaceRouteRef(second.at)) return false;
  const start = { x: first.at[0] * m, z: first.at[1] };
  const ahead = { x: second.at[0] * m, z: second.at[1] };

  // Does the world already reach far enough behind the start? Asked of the
  // built network rather than of the road's vertices, because what matters is
  // where the drivable surface ends, not where a polyline does.
  const len = Math.hypot(ahead.x - start.x, ahead.z - start.z);
  if (len < 1e-6) return false;
  const bx = (start.x - ahead.x) / len;
  const bz = (start.z - ahead.z) / len;
  if (network.sdf(start.x + bx * CAMERA_NEEDS_M, start.z + bz * CAMERA_NEEDS_M) < 0) return false;

  const band = pieces.find(
    (p): p is BandPiece => p.kind === "band" && (first.road ? p.id === first.road : false),
  );
  if (!band || band.pts.length < 2) return false;

  // Which end of this road is the route starting from? The one nearer the
  // start point and further from where the route is heading.
  const head = band.pts[0]!;
  const tail = band.pts[band.pts.length - 1]!;
  const atHead = dist2(head, start) <= dist2(tail, start);
  const end = atHead ? head : tail;
  const inward = atHead ? band.pts[1]! : band.pts[band.pts.length - 2]!;

  // Only extend if the road really does stop near the start; a road that
  // already runs well behind it needs nothing.
  const behind = Math.sqrt(dist2(end, start));
  if (behind >= START_APRON_M) return false;
  // The end we grow must be the one pointing AWAY from where the route is
  // heading; growing the other one would push road across the map rather than
  // behind the start line.
  if (dist2(end, ahead) > dist2(inward, ahead)) {
    const seg = Math.hypot(end.x - inward.x, end.z - inward.z);
    if (seg < 1e-6) return false;
    const ux = (end.x - inward.x) / seg;
    const uz = (end.z - inward.z) / seg;
    const grow = START_APRON_M - behind;
    const extended = { x: end.x + ux * grow, z: end.z + uz * grow };
    if (atHead) band.pts[0] = extended;
    else band.pts[band.pts.length - 1] = extended;
    return true;
  }
  return false;
}

function dist2(a: Vec2, b: Vec2): number {
  return (a.x - b.x) ** 2 + (a.z - b.z) ** 2;
}
